import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Plus, Brain, ShieldAlert, Flame, Globe2, FileBarChart, ArrowRight, MessageSquare, Lock, CalendarDays,
  CalendarClock, Gift, Video, CalendarPlus,
} from "lucide-react";
import { portalApi } from "@/lib/portalApi";
import { usePortalAuth } from "@/context/PortalAuthContext";
import { PortalLayout } from "@/components/portal/PortalLayout";
import { AddPasswordCard } from "@/components/portal/AddPasswordCard";
import { ResearchConsentCard } from "@/components/portal/ResearchConsentCard";
import { TraitBar } from "@/components/portal/Primitives";
import { BEHAVIOR_LABELS, BIG_FIVE_TRAITS, TRAIT_LABELS } from "@/data/questionnaires";

const Stat = ({ icon: Icon, value, label, testid }) => (
  <div className="border border-slate-800 bg-[#0B1220]/70 p-5" data-testid={testid}>
    <Icon size={16} className="text-[#D4AF37] mb-4" />
    <p className="font-serif text-2xl md:text-3xl text-white leading-none">{value}</p>
    <p className="text-[11px] uppercase tracking-widest text-slate-500 mt-2">{label}</p>
  </div>
);

const nextStep = (user, incidents) => {
  if (!user?.big_five_completed)
    return {
      eyebrow: "Step 1 of 3",
      title: "Map your own personality first",
      body: "Your vulnerabilities come from the interaction between your traits and your leader's. 20 questions, about three minutes.",
      cta: "Start Big Five assessment",
      to: "/portal/big-five",
      testid: "next-step-big-five",
    };
  if (!incidents.length)
    return {
      eyebrow: "Step 2 of 3",
      title: "Log what happened",
      body: "Describe one incident in your own words — type it or dictate it. Your AI advisor responds with a boss profile and tactics within seconds.",
      cta: "Log your first incident",
      to: "/portal/incident/new",
      testid: "next-step-incident",
    };
  const unscored = incidents.find((i) => !i.dark_triad_scores);
  if (unscored)
    return {
      eyebrow: "Step 3 of 3",
      title: "Profile your leader",
      body: "Rate 15 observable behaviours to place your leader on the dark triad — narcissism, Machiavellianism and psychopathy.",
      cta: "Run Leader D3 assessment",
      to: `/portal/incident/${unscored.incident_id}/leader-d3`,
      testid: "next-step-dark-triad",
    };
  return {
    eyebrow: "Ready",
    title: "Build your vulnerability report",
    body: "Both profiles are in. See exactly which trait interactions this leader can exploit, and what to do about each one.",
    cta: "Open vulnerability report",
    to: "/portal/report",
    testid: "next-step-report",
  };
};

export default function PortalDashboard() {
  const { user } = usePortalAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [limit, setLimit] = useState(null);
  const [progress, setProgress] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [s, i, l, p, up] = await Promise.all([
          portalApi.stats(), portalApi.incidents(), portalApi.incidentLimit(), portalApi.progress(),
          portalApi.upcomingSessions().catch(() => []),
        ]);
        setStats(s);
        setIncidents(i);
        setLimit(l);
        setProgress(p);
        setSessions(up || []);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <PortalLayout>
        <div className="flex justify-center py-24" data-testid="dashboard-loading">
          <Loader2 className="animate-spin text-[#D4AF37]" size={26} />
        </div>
      </PortalLayout>
    );
  }

  const step = nextStep(user, incidents);
  const canLog = !limit || limit.unlimited || limit.remaining > 0;

  return (
    <PortalLayout>
      <div data-testid="portal-dashboard">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-10">
          <div>
            <p className="text-[#D4AF37] text-xs uppercase tracking-[0.28em] mb-3">Client Portal</p>
            <h1 className="font-serif text-3xl md:text-4xl text-white">
              {user?.name?.split(" ")[0] ? `Hello, ${user.name.split(" ")[0]}` : "Your dashboard"}
            </h1>
          </div>
          <button
            onClick={() => navigate(canLog ? "/portal/incident/new" : "/portal/pricing")}
            data-testid="dashboard-log-incident-btn"
            className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-slate-950 px-6 py-3 text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#B5952F] transition-colors"
          >
            {canLog ? <Plus size={14} /> : <Lock size={14} />}
            Log an incident
          </button>
        </div>

        {sessions.length > 0 && (
          <div className="border border-[#D4AF37]/40 bg-[#0B1220]/80 p-6 md:p-8 mb-10" data-testid="dashboard-upcoming-session">
            <p className="text-[#D4AF37] text-[11px] uppercase tracking-[0.28em] mb-4">Your next session</p>
            {sessions.slice(0, 2).map((s) => (
              <div
                key={s.booking_id}
                className="flex flex-col lg:flex-row lg:items-center justify-between gap-5 py-4 border-t border-slate-800 first:border-t-0 first:pt-0"
                data-testid={`upcoming-session-${s.booking_id}`}
              >
                <div>
                  <p className="font-serif text-lg md:text-xl text-white mb-1.5">
                    {new Date(s.starts_at).toLocaleString(undefined, {
                      weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit",
                    })}
                  </p>
                  <p className="text-slate-400 text-sm">
                    {s.session_type === "initial" ? "Initial consultation · 60 min" : "Follow-up session · 30 min"}
                    {" · shown in your local time"}
                  </p>
                  <div className="flex flex-wrap gap-4 mt-3">
                    {s.add_to_google_url && (
                      <a
                        href={s.add_to_google_url}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`upcoming-gcal-${s.booking_id}`}
                        className="text-[#D4AF37] text-[11px] uppercase tracking-[0.2em] inline-flex items-center gap-1.5 hover:underline"
                      >
                        <CalendarPlus size={12} /> Add to Google Calendar
                      </a>
                    )}
                    {s.calendar_token && (
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/booking/${s.booking_id}/calendar.ics?t=${s.calendar_token}`}
                        data-testid={`upcoming-ics-${s.booking_id}`}
                        className="text-slate-400 text-[11px] uppercase tracking-[0.2em] inline-flex items-center gap-1.5 hover:text-[#D4AF37]"
                      >
                        <CalendarDays size={12} /> Apple / Outlook (.ics)
                      </a>
                    )}
                  </div>
                </div>
                {s.meeting_link && (
                  <a
                    href={s.meeting_link}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`upcoming-join-${s.booking_id}`}
                    className="shrink-0 inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-slate-950 px-6 py-3 text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#B5952F] transition-colors"
                  >
                    <Video size={14} /> Join the session
                  </a>
                )}
              </div>
            ))}
          </div>
        )}

        <div
          className="border border-[#D4AF37]/40 bg-gradient-to-br from-[#D4AF37]/[0.08] to-transparent p-6 md:p-9 mb-10"
          data-testid="dashboard-next-step"
        >
          <p className="text-[#D4AF37] text-[11px] uppercase tracking-[0.28em] mb-3">{step.eyebrow}</p>
          <h2 className="font-serif text-xl md:text-2xl text-white mb-3">{step.title}</h2>
          <p className="text-slate-400 text-sm max-w-2xl leading-relaxed mb-6">{step.body}</p>
          <Link
            to={step.to}
            data-testid={step.testid}
            className="inline-flex items-center gap-2 text-[#D4AF37] text-xs uppercase tracking-[0.2em] border-b border-[#D4AF37]/40 pb-1 hover:border-[#D4AF37] transition-colors"
          >
            {step.cta} <ArrowRight size={14} />
          </Link>
        </div>

        {progress?.reassessment_due && (
          <div
            className="border border-[#D4AF37]/40 bg-[#D4AF37]/[0.06] p-5 md:p-6 mb-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
            data-testid="dashboard-checkin-banner"
          >
            <div className="flex items-start gap-3.5">
              <CalendarClock className="text-[#D4AF37] shrink-0 mt-0.5" size={17} />
              <div>
                <p className="text-white text-sm mb-1">Your quarterly check-in is due</p>
                <p className="text-slate-400 text-sm">
                  You last measured this {progress.days_since_assessment} days ago. Re-take it to see what has actually shifted.
                </p>
              </div>
            </div>
            <Link
              to="/portal/progress"
              data-testid="dashboard-checkin-link"
              className="shrink-0 text-[#D4AF37] text-xs uppercase tracking-[0.2em] hover:underline"
            >
              See my progress →
            </Link>
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          <Stat icon={FileBarChart} value={stats?.incident_count ?? 0} label="Incidents logged" testid="stat-incidents" />
          <Stat icon={Flame} value={stats?.streak ?? 0} label="Day streak" testid="stat-streak" />
          <Stat icon={Brain} value={stats?.total_cases_in_dataset?.toLocaleString() ?? "—"} label="Cases in research base" testid="stat-cases" />
          <Stat icon={Globe2} value={stats?.total_countries ?? "—"} label="Countries studied" testid="stat-countries" />
        </div>

        {limit && !limit.unlimited && (
          <div className="border border-slate-800 bg-[#0B1220]/70 p-5 mb-12 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="dashboard-limit-banner">
            <p className="text-slate-400 text-sm">
              Free plan — <span className="text-white">{limit.used} of {limit.limit}</span> incidents used this month.
            </p>
            <Link to="/portal/pricing" className="text-[#D4AF37] text-xs uppercase tracking-[0.2em] hover:underline shrink-0" data-testid="dashboard-upgrade-link">
              Go unlimited →
            </Link>
          </div>
        )}

        {user?.big_five_completed && user?.big_five_scores && (
          <section className="mb-12" data-testid="dashboard-big-five">
            <div className="flex items-baseline justify-between mb-6">
              <h2 className="font-serif text-xl md:text-2xl text-white">Your personality profile</h2>
              <Link to="/portal/big-five" className="text-xs uppercase tracking-widest text-slate-500 hover:text-[#D4AF37]" data-testid="dashboard-retake-big-five">
                Retake
              </Link>
            </div>
            <div className="border border-slate-800 bg-[#0B1220]/70 p-6 md:p-8 grid md:grid-cols-2 gap-x-12 gap-y-6">
              {BIG_FIVE_TRAITS.map((trait) => (
                <TraitBar key={trait} label={TRAIT_LABELS[trait]} value={user.big_five_scores[trait] ?? 0} testid={`dashboard-trait-${trait}`} />
              ))}
            </div>
          </section>
        )}

        <section data-testid="dashboard-incident-list">
          <h2 className="font-serif text-xl md:text-2xl text-white mb-6">Your incident log</h2>
          {!incidents.length ? (
            <div className="border border-dashed border-slate-800 p-10 text-center" data-testid="dashboard-no-incidents">
              <p className="text-slate-500 text-sm">Nothing logged yet. Your entries stay private to your account.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {incidents.map((inc) => (
                <Link
                  key={inc.incident_id}
                  to={`/portal/incident/${inc.incident_id}`}
                  data-testid={`incident-card-${inc.incident_id}`}
                  className="block border border-slate-800 bg-[#0B1220]/70 p-5 md:p-6 hover:border-[#D4AF37]/50 transition-colors group"
                >
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex flex-wrap gap-2">
                      {inc.behaviors.map((b) => (
                        <span key={b} className="text-[10px] uppercase tracking-widest border border-slate-700 text-slate-400 px-2 py-1">
                          {BEHAVIOR_LABELS[b] || b}
                        </span>
                      ))}
                    </div>
                    <span className="flex items-center gap-2 text-slate-600 text-xs shrink-0">
                      <CalendarDays size={12} />
                      {new Date(inc.incident_date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                    </span>
                  </div>
                  <p className="text-slate-300 text-sm leading-relaxed line-clamp-2 mb-4 group-hover:text-white transition-colors">
                    {inc.description}
                  </p>
                  <div className="flex items-center gap-5 text-[11px] uppercase tracking-widest text-slate-500">
                    <span className="flex items-center gap-1.5">
                      <MessageSquare size={12} /> {inc.message_count} messages
                    </span>
                    <span className={`flex items-center gap-1.5 ${inc.dark_triad_scores ? "text-[#D4AF37]" : ""}`}>
                      <ShieldAlert size={12} /> {inc.dark_triad_scores ? "Leader profiled" : "D3 pending"}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        <div className="mt-14 border border-slate-800 bg-[#0B1220]/70 p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-5" data-testid="dashboard-referral-cta">
          <div className="flex items-start gap-4">
            <Gift className="text-[#D4AF37] shrink-0 mt-0.5" size={20} />
            <div>
              <h3 className="font-serif text-lg text-white mb-2">Know someone with the same boss problem?</h3>
              <p className="text-slate-400 text-sm max-w-xl leading-relaxed">
                Send them your invite link. When they upgrade to Premium or book their first session, you get a month of Premium free.
              </p>
            </div>
          </div>
          <Link
            to="/portal/refer"
            className="shrink-0 border border-[#D4AF37] text-[#D4AF37] px-6 py-3 text-xs uppercase tracking-[0.2em] hover:bg-[#D4AF37] hover:text-slate-950 transition-colors text-center"
            data-testid="dashboard-refer-link"
          >
            Get my link
          </Link>
        </div>

        <div className="mt-5 border border-slate-800 bg-[#0B1220]/70 p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-5" data-testid="dashboard-booking-cta">
          <div>
            <h3 className="font-serif text-lg text-white mb-2">Want a behavioural scientist's read on this?</h3>
            <p className="text-slate-400 text-sm max-w-xl leading-relaxed">
              Bring your report to a 1-on-1 session and leave with a plan built for your exact situation.
            </p>
          </div>
          <Link
            to="/"
            className="shrink-0 border border-[#D4AF37] text-[#D4AF37] px-6 py-3 text-xs uppercase tracking-[0.2em] hover:bg-[#D4AF37] hover:text-slate-950 transition-colors text-center"
            data-testid="dashboard-book-session-link"
          >
            Book a session
          </Link>
        </div>

        <AddPasswordCard />
        <ResearchConsentCard />
      </div>
    </PortalLayout>
  );
}
